// Last updated: 2026-06-10T00:00:00Z (Chakra v3 migration)
// =============================================
// Sidebar Component - Admin Navigation Restriction
//
// This component provides navigation for the ExamGate platform. Admin-only functions
// (Manage Exams, Edit Questions, Exam Generator, Function Log, Stripe Webhooks) are only visible
// to users with the role "admin". The isAdmin prop should be set based on the user's role field in Firestore.
//
// Features:
// - Automatic environment detection for URLs
// - Admin-only Stripe webhook dashboard access
// - Proper navigation without page reloads
// - Visual feedback for active navigation items
//
// Last updated: 2024-06-29 07:00:00 UTC
// =============================================

import React from "react";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import { FaListAlt, FaBook, FaEdit, FaExternalLinkAlt, FaClipboardCheck } from "react-icons/fa";
import { FiFileText, FiCreditCard, FiCpu, FiHelpCircle, FiUsers } from "react-icons/fi";
import {
  Box,
  VStack,
  Icon,
  Drawer,
  Portal,
} from '@chakra-ui/react';
import { useColorMode } from './components/ui/color-mode';
import { Link } from 'react-router-dom';

export default function Sidebar({ activePath = "/tests", isAdmin = false, canManageQuestions = false, isMobile, isOpen, onClose }) {
  const { colorMode } = useColorMode();
  
  // =============================================
  // Dynamic URL Generation for Environment Compatibility
  //
  // Automatically detects if running locally or in production
  // and generates appropriate URLs for external links.
  // This ensures the sidebar works correctly in both environments.
  // =============================================
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const baseUrl = isLocalhost ? 'http://localhost:3000' : window.location.origin;
  
  console.log('[Sidebar] Environment detected:', {
    hostname: window.location.hostname,
    isLocalhost: isLocalhost,
    baseUrl: baseUrl,
    isAdmin: isAdmin
  });

  // =============================================
  // Navigation Menu Items Configuration
  //
  // Defines all navigation items with their properties:
  // - label: Display text for the menu item
  // - icon: React icon component to display
  // - path: Internal route path or external URL
  // - show: Boolean to control visibility
  // - isExternal: Boolean to indicate external links
  // =============================================
  const menuItems = [
    { label: "Dashboard", icon: FiCreditCard, path: "/dashboard", show: true, isExternal: false },
    { label: "Take a Test", icon: FiFileText, path: "/tests", show: true, isExternal: false },
    { label: "Subscriptions", icon: FiCreditCard, path: "/subscriptions", show: true, isExternal: false },
    { label: "My Tests", icon: FaListAlt, path: "/my-tests", show: true, isExternal: false },
    // Admin functions below: show only if isAdmin is true
    { label: "Manage Exams", icon: FaBook, path: "/exams", show: isAdmin, isExternal: false },
    { label: "Landing Page", icon: FiFileText, path: "/landing-page", show: isAdmin, isExternal: false },
    { label: "Users", icon: FiUsers, path: "/admin-users", show: isAdmin, isExternal: false },
    { label: "RAG System", icon: FiCpu, path: "/rag-system", show: canManageQuestions, isExternal: false },
    { label: "Add/Edit Questions", icon: FaEdit, path: "/edit-questions", show: canManageQuestions, isExternal: false },
    { label: "Exam Generator", icon: FaEdit, path: "/exam-generator", show: canManageQuestions, isExternal: false },
    { label: "PBQ Review", icon: FaClipboardCheck, path: "/pbq-review", show: canManageQuestions, isExternal: false },
    { label: "Function Log", icon: FiFileText, path: "/function-log", show: isAdmin, isExternal: false },
    { label: "Admin Help", icon: FiHelpCircle, path: "/admin-help", show: isAdmin, isExternal: false },
    { 
      label: "Stripe Webhooks", 
      icon: FaExternalLinkAlt, 
      path: "https://dashboard.stripe.com/workbench/webhooks", 
      show: isAdmin, 
      isExternal: true 
    },
  ];

  // =============================================
  // Admin Section Divider Logic
  //
  // Finds the first admin function to insert a visual divider
  // that separates regular user functions from admin functions.
  // =============================================
  const firstAdminIdx = menuItems.findIndex(item => item.show && item.label === "Manage Exams");

  // =============================================
  // Navigation Item Rendering Function
  //
  // Renders individual navigation items with proper styling
  // and handles both internal routes and external links.
  // =============================================
  const renderMenuItem = (item, idx) => {
    const isActive = activePath === item.path;
    const itemStyle = {
      display: "flex",
      alignItems: "center",
      textDecoration: "none",
      color: isActive ? (colorMode === 'dark' ? 'brand.200' : 'brand.700') : (colorMode === 'dark' ? 'gray.300' : 'gray.700'),
      bg: isActive ? (colorMode === 'dark' ? 'whiteAlpha.200' : 'brand.50') : 'transparent',
      borderRadius: '10px',
      padding: "9px 14px",
      fontWeight: isActive ? 600 : 400,
      fontSize: "0.875rem",
      borderLeft: isActive ? '3px solid' : '3px solid transparent',
      borderColor: isActive ? (colorMode === 'dark' ? 'brand.400' : 'brand.500') : 'transparent',
      boxShadow: isActive ? (colorMode === 'dark' ? 'inset 0 0 0 1px rgba(125, 211, 252, 0.18)' : '0 1px 2px rgba(30, 64, 175, 0.08)') : 'none',
      transition: 'all 0.15s ease',
      _hover: {
        bg: isActive ? (colorMode === 'dark' ? 'whiteAlpha.200' : 'brand.50') : (colorMode === 'dark' ? 'whiteAlpha.100' : 'gray.100'),
      },
      _focusVisible: {
        outline: '2px solid',
        outlineColor: colorMode === 'dark' ? 'brand.300' : 'brand.500',
        outlineOffset: '2px',
      },
    };

    const handleClick = () => {
      console.log('[Sidebar] Navigating to:', item.path, 'External:', item.isExternal);
    };

    // =============================================
    // External Link Rendering
    //
    // Renders external links (like Stripe dashboard) with proper
    // attributes for opening in new tab and security.
    // =============================================
    if (item.isExternal) {
      return (
        <Box
          as="a"
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          {...itemStyle}
          onClick={handleClick}
        >
          <Icon as={item.icon} fontSize="md" marginRight={3} color={colorMode === 'dark' ? 'brand.200' : 'brand.500'} />
          {item.label}
        </Box>
      );
    }

    // =============================================
    // Internal Route Rendering
    //
    // Renders internal navigation using React Router Link
    // for proper SPA navigation without page reloads.
    // =============================================
    return (
      <Box
        as={Link}
        to={item.path}
        {...itemStyle}
        onClick={handleClick}
      >
        <Icon as={item.icon} fontSize="md" marginRight={3} color={colorMode === 'dark' ? 'brand.200' : 'brand.500'} />
        {item.label}
      </Box>
    );
  };

  // =============================================
  // Sidebar Content Component
  //
  // Main sidebar content with navigation items and
  // visual dividers for admin sections. zIndex is set to 100 for safety.
  // =============================================
  const sidebarContent = (
    <Box
      width="270px"
      bg={colorMode === 'dark' ? 'gray.900' : 'gray.50'}
      color={colorMode === 'dark' ? 'gray.100' : 'gray.800'}
      display="flex"
      flexDirection="column"
      zIndex={100}
      position={{ base: isMobile ? 'fixed' : 'static', md: 'static' }}
      top={isMobile ? 0 : 'auto'}
      left={isMobile ? 0 : 'auto'}
      height={isMobile ? '100vh' : 'auto'}
      boxShadow={isMobile ? '2xl' : 'inset -1px 0 0 rgba(148,163,184,0.12)'}
      borderRight={isMobile ? 'none' : '1px solid'}
      borderColor={isMobile ? 'transparent' : (colorMode === 'dark' ? 'gray.800' : 'gray.200')}
    >
      <Box flex={1} pt={4}>
        <SimpleBar autoHide={false} style={{ height: '100%' }}>
          <VStack as="nav" gap={3} align="stretch" px={3}>
            {/* Spacer to prevent overlap with close button in mobile mode */}
            {isMobile && <Box height="20px" />}
            {/* Top spacer above Dashboard */}
            <Box height="8px" />
            {menuItems.map((item, idx) => item.show && (
              <React.Fragment key={item.label}>
                {/* Visual divider before first admin function */}
                {idx === firstAdminIdx && idx !== 0 && (
                  <Box height="1px" bg={colorMode === 'dark' ? 'gray.700' : 'gray.200'} my={3} mx={2} />
                )}
                {renderMenuItem(item, idx)}
              </React.Fragment>
            ))}
          </VStack>
        </SimpleBar>
      </Box>
    </Box>
  );

  // =============================================
  // Mobile Drawer Rendering
  //
  // Renders sidebar as a drawer overlay on mobile devices
  // with proper touch interactions and accessibility.
  // =============================================
  if (isMobile) {
    return (
      <Drawer.Root open={isOpen} placement="start" onOpenChange={(e) => { if (!e.open) onClose() }}>
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content maxW="270px" position="relative">
              <Drawer.CloseTrigger
                zIndex={2000}
                position="absolute"
                top="16px"
                right="16px"
              />
              <Drawer.Body p={0}>
                <Box mt="96px">
                  {sidebarContent}
                </Box>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    );
  }

  // =============================================
  // Desktop Static Sidebar
  //
  // Renders sidebar as a static component on desktop
  // for persistent navigation access.
  // =============================================
  return sidebarContent;
} 
